import { useEffect, useState } from 'react';
import { IDEA_LIMITS } from '@shared/validate';
import { tagTone } from '@shared/format';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, Spinner } from '../../components/Card';
import { Switch, TextArea, TextInput } from '../../components/Field';
import { toast } from '../../components/Toast';
import { errorMessage } from '../../lib/api';
import { useSettingsStore } from './settingsStore';

/**
 * 系统设置。
 *
 * 这里最关键的不是开关，而是「团队能力清单」：它是研判提示词里唯一描述团队现状的部分。
 * 不填，模型就会建议团队去造已经有的轮子 —— 可复用资产那一节会永远是空的。
 */
export function SettingsPage() {
  const settings = useSettingsStore((state) => state.settings);
  const loading = useSettingsStore((state) => state.loading);
  const loaded = useSettingsStore((state) => state.loaded);
  const load = useSettingsStore((state) => state.load);
  const save = useSettingsStore((state) => state.save);

  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [customTag, setCustomTag] = useState('');

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const submit = async () => {
    setSaving(true);
    try {
      await save({
        aiEnabled: draft.aiEnabled,
        aiDailyLimit: draft.aiDailyLimit,
        aiModel: draft.aiModel,
        tags: draft.tags,
        capabilities: draft.capabilities,
      });
      toast.ok('设置已保存');
    } catch (error) {
      toast.critical('保存失败', errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tag = customTag.trim().replace(/^#/, '');
    if (tag === '' || tag.length > IDEA_LIMITS.tagMax) return;
    if (draft.tags.includes(tag) || draft.tags.length >= IDEA_LIMITS.tagCatalogMax) return;
    setDraft({ ...draft, tags: [...draft.tags, tag] });
    setCustomTag('');
  };

  if (!loaded && loading) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Spinner /> 正在读取设置…
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="max-w-4xl">
      <PageHeader
        title="系统设置"
        description="AI 研判的开关与额度、看板的分类标签，以及作为研判上下文的团队能力清单。"
        actions={
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            保存
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle subtitle="模型与密钥通过 Worker 环境变量注入，界面上不能也不应该填写密钥">AI 研判</CardTitle>
          <Badge tone={settings.aiConfigured ? 'ok' : 'critical'}>{settings.aiConfigured ? '已配置' : '未配置'}</Badge>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {!settings.aiConfigured ? (
            <p className="rounded-md border border-caution/50 bg-caution-soft px-3 py-2 text-[11px] leading-relaxed text-caution">
              尚未注入 <code className="font-mono">AI_API_KEY</code>：需要在 Worker 上执行{' '}
              <code className="font-mono">wrangler secret put AI_API_KEY</code>，并确认{' '}
              <code className="font-mono">AI_BASE_URL</code> 与 <code className="font-mono">AI_MODEL</code> 指向可用的 OpenAI 兼容端点，
              然后重新部署。在此之前「发起 AI 研判」会返回明确的配置提示，而不是报错白屏。
            </p>
          ) : null}

          <Switch
            checked={draft.aiEnabled}
            onChange={(next) => setDraft({ ...draft, aiEnabled: next })}
            label="允许发起 AI 研判"
            hint="关闭后所有成员都无法发起研判，已有结论仍然可见。"
          />

          <div className="grid gap-4 md:grid-cols-2">
            <TextInput
              label="每人每日研判额度"
              type="number"
              min={0}
              max={IDEA_LIMITS.aiDailyLimitMax}
              value={String(draft.aiDailyLimit)}
              onChange={(event) => setDraft({ ...draft, aiDailyLimit: Number(event.target.value) || 0 })}
              hint="0 表示不限。按 UTC 零点重置，界面上的「今天」以 UTC 计。"
            />
            <TextInput
              label="模型名（展示用）"
              value={draft.aiModel}
              onChange={(event) => setDraft({ ...draft, aiModel: event.target.value })}
              hint="实际调用以 Worker 的 AI_MODEL 为准，这里只是让人知道在用哪个模型。"
            />
          </div>

          <p className="text-[11px] leading-relaxed text-fg-subtle">
            额度与去重是两道独立的闸门：同一创意在研判进行中不允许再次发起，
            因此「反复点按钮」不会烧掉额度。
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle subtitle="看板筛选与提交时的快捷选择">分类标签</CardTitle>
          <Badge tone="neutral">{draft.tags.length} / {IDEA_LIMITS.tagCatalogMax}</Badge>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5">
            {draft.tags.length === 0 ? (
              <span className="text-[11px] text-fg-subtle">还没有标签，提交创意时只能自己手写。</span>
            ) : (
              draft.tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  title="点击移除"
                  onClick={() => setDraft({ ...draft, tags: draft.tags.filter((item) => item !== tag) })}
                >
                  <Badge tone={tagTone(tag)}>{tag} ✕</Badge>
                </button>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
            <TextInput
              placeholder="新增一个标签"
              value={customTag}
              maxLength={IDEA_LIMITS.tagMax}
              onChange={(event) => setCustomTag(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addTag();
              }}
            />
            <Button size="sm" variant="default" onClick={addTag} disabled={customTag.trim() === ''}>
              添加
            </Button>
          </div>
          <p className="text-[11px] text-fg-subtle">
            标签只是筛选与归类的辅助，不做权限控制。移除某个标签不会影响已经用它标记过的创意。
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle subtitle="这是研判提示词里描述团队现状的唯一来源">团队能力清单</CardTitle>
          <Badge tone="neutral">{(draft.capabilities.length / IDEA_LIMITS.capabilityMax) * 100 < 1 ? '<1%' : `${Math.round((draft.capabilities.length / IDEA_LIMITS.capabilityMax) * 100)}%`}</Badge>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          <TextArea
            rows={16}
            maxLength={IDEA_LIMITS.capabilityMax}
            value={draft.capabilities}
            onChange={(event) => setDraft({ ...draft, capabilities: event.target.value })}
            placeholder={'现有系统：\n- ……\n\n技术栈：\n- ……\n\n可复用资产：\n- ……'}
          />
          <div className="rounded-md border border-line bg-surface-2 p-3 text-[11px] leading-relaxed text-fg-muted">
            <p className="font-medium text-fg">怎么写才有用</p>
            <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
              <li>写清已有系统与它们的能力边界，模型才能判断「改造」还是「重做」。</li>
              <li>写清技术栈与限制（例如能跑 Workers、用 SQLite），避免建议不可落地的架构。</li>
              <li>写清可复用资产的具体名字，研判报告的「可复用资产」一节只会引用这里出现过的内容。</li>
              <li>超过 4000 字的部分在注入提示词时会被截断，结论以开头部分为准。</li>
            </ul>
          </div>
        </CardBody>
      </Card>

      <div className="flex items-center gap-2">
        <Button variant="primary" loading={saving} onClick={() => void submit()}>
          保存设置
        </Button>
        <Button variant="ghost" disabled={saving} onClick={() => setDraft(settings)}>
          放弃改动
        </Button>
      </div>
    </PageContainer>
  );
}
